import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ArrowDataTransferHorizontalIcon,
  CallAdd02Icon,
  CallEnd01Icon,
  Cancel01Icon,
  CheckmarkCircle02Icon,
  DialpadCircle01Icon,
  Mic01Icon,
  MicOff01Icon,
  PauseIcon,
  PlayIcon,
  User02Icon,
  UserAdd01Icon,
  UserCircleIcon
} from "@hugeicons-pro/core-stroke-rounded";
import { Button, ButtonSize, ButtonVariant } from "shared/components";
import { Avatar } from "shared/components/Avatar";
import { cx } from "shared/utils";
import {
  UNKNOWN_CALLER_LABEL,
  isCallerUnknown,
  transferSourceLabel
} from "shared/utils/transferred-call";
import { formatPhoneDisplay } from "../views/phone-numbers/lib/format";
import {
  firstName,
  useSoftphone,
  type ActiveCall,
  type ActiveTransfer,
  type ActiveVoicemailDrop
} from "./SoftphoneProvider";
import { CONSULT_PHASES, THREE_WAY_PHASES } from "./transferPhases";
import { ConsultView, ThreeWayView } from "./ConsultView";
import { TransferPanel, type TransferPanelPurpose } from "./TransferPanel";
import { useElapsed } from "./useElapsed";
import { VoicemailGlyph } from "shared/components/VoicemailGlyph";
import { VoicemailLeavingStatus, VoicemailPanel } from "./VoicemailPanel";
import { usePickableVoicemails } from "./usePickableVoicemails";
import { CallKeypad } from "./CallKeypad";
import { ControlButton } from "./ControlButton";
import { describeCall, formatDuration, type CallDisplay } from "./callDisplay";
import { SOFTPHONE_OVERLAY_Z_CLASS } from "./stacking";
import { useDraggableOverlay, type DragOffset } from "./useDraggableOverlay";

const ACTIVE_AVATAR_COLOR = "#29B6F6";
const ENDED_AVATAR_COLOR = "#9CA3AF";

interface CallScreenProps {
  // Live in-call duration, ticked once a second by the overlay.
  elapsedSeconds: number;
  // Collapse the expanded card back to the floating pill.
  onMinimize: () => void;
  // Opens the in-call Create-lead form (inbound calls with no matched lead).
  onCreateLead: () => void;
  // Dial-pad visibility, owned by the overlay so the pill can open it too.
  keypadOpen: boolean;
  onKeypadOpenChange: (open: boolean) => void;
  // Drag position, owned by the overlay so it survives minimize/expand.
  dragOffset: DragOffset;
  onDragOffsetChange: (offset: DragOffset) => void;
}

/**
 * Expanded in-call card (CRM-811, Figma 22148:162504; inbound states CRM-905,
 * Figma 23472:90575). Floats bottom-right and walks the call through Ringing →
 * mm:ss (with mute) → No answer / Call ended · mm:ss / Can't reach this
 * number. Inbound calls add a middle action: Contact details (matched lead) or
 * Create lead. Once the call is answered, Keypad opens the DTMF dial pad for
 * IVRs and call screening (CRM-1190). An answered call can be transferred
 * (CRM-1747), put on hold or joined by a third person (CRM-1818). The X
 * collapses a live call to the pill; for a terminal call it dismisses the
 * card. Drag it anywhere on screen to uncover what it sits on top of
 * (CRM-1112).
 */
export function CallScreen({
  elapsedSeconds,
  onMinimize,
  onCreateLead,
  keypadOpen,
  onKeypadOpenChange,
  dragOffset,
  onDragOffsetChange
}: CallScreenProps) {
  const {
    activeCall,
    status,
    isMuted,
    callDurationSeconds,
    hangup,
    toggleMute,
    sendDtmf,
    dismissCall,
    transfer,
    callNotice,
    voicemailDrop
  } = useSoftphone();
  // The call the Transfer call view (CRM-1747) — or its Add to call twin
  // (CRM-1818) — is open for; it replaces the card's body while open.
  const [picker, setPicker] = useState<{
    callId: number;
    purpose: TransferPanelPurpose;
  } | null>(null);
  // The same for the Leave a voicemail view (CRM-1830).
  const [voicemailPickerCallId, setVoicemailPickerCallId] = useState<
    number | null
  >(null);

  const { nodeRef, dragClassName, style, dragProps } = useDraggableOverlay({
    offset: dragOffset,
    onOffsetChange: onDragOffsetChange
  });

  // Computed before the early return so the hook below can key off it.
  const view = describeCall(status, { elapsedSeconds, callDurationSeconds });
  const ownTransfer = ownTransferOf(activeCall, transfer);
  // CRM-1818: the caller can't hear a tone while on hold.
  const onHold = ownTransfer?.phase === "on-hold";
  const keypadAvailable = view.showKeypad && !onHold;

  // The call ended (or fell back to ringing) with the pad open — there is
  // nothing left to send tones to. Resetting it per call lives in CallOverlay.
  useEffect(() => {
    if (!keypadAvailable) onKeypadOpenChange(false);
  }, [keypadAvailable, onKeypadOpenChange]);

  const canTransfer = view.showMute && !activeCall?.transferUnavailable;
  const offerVoicemail = canOfferVoicemail(activeCall, view, transfer);
  const voicemails = usePickableVoicemails(offerVoicemail);

  if (!activeCall) return null;

  const displayName = activeCall.toLabel?.trim();
  const isInbound = activeCall.direction === "inbound";
  const { displayNumber, transferSource } = describeParty(activeCall);
  const callerName = displayName || displayNumber;
  const elapsedLabel = view.statusLine;
  const openPicker = (purpose: TransferPanelPurpose) =>
    setPicker({ callId: activeCall.callId, purpose });
  const body =
    voicemailBody({
      drop: voicemailDrop?.callId === activeCall.callId ? voicemailDrop : null,
      pickerOpen: voicemailPickerCallId === activeCall.callId && offerVoicemail,
      callerName,
      displayName,
      elapsedLabel,
      onClosePicker: () => setVoicemailPickerCallId(null)
    }) ??
    transferBody({
      transfer: ownTransfer,
      // Only on a live, answered call; a transfer that started closes it.
      pickerPurpose: canTransfer ? pickerPurposeFor(picker, activeCall) : null,
      callerName,
      elapsedLabel,
      durationSeconds: callDurationSeconds,
      // The 3-way call view (CRM-1818) carries the call's own clock and pad.
      live: view.showMute,
      keypadOpen,
      onKeypadOpenChange,
      onDismiss: dismissCall,
      onClosePicker: () => setPicker(null)
    });

  return (
    <div
      ref={nodeRef}
      role="dialog"
      aria-label="Active call"
      style={style}
      {...dragProps}
      className={cx(
        // The open dial pad makes the card roughly twice as tall, so cap it to
        // the viewport instead of letting it run off the top of short screens.
        "fixed bottom-4 right-4 max-h-[calc(100vh-2rem)] w-[360px] max-w-[calc(100vw-2rem)] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl ring-1 ring-alpha-100",
        dragClassName,
        SOFTPHONE_OVERLAY_Z_CLASS
      )}
    >
      <button
        type="button"
        aria-label={view.isTerminal ? "Dismiss" : "Minimize"}
        onClick={view.isTerminal ? dismissCall : onMinimize}
        className="absolute right-3 top-3 grid size-8 place-items-center rounded-full text-content-300 transition hover:bg-alpha-40 hover:text-content-1000"
      >
        <HugeiconsIcon icon={Cancel01Icon} size={20} />
      </button>

      {body ?? (
        <div className="flex flex-col items-center text-center">
          <PartyHeader
            displayName={displayName}
            displayNumber={displayNumber}
            transferSource={transferSource}
            addedBy={activeCall.addedByName}
            live={view.isLive}
          />
          <CallStatus
            view={view}
            notice={callNotice}
            transfer={ownTransfer}
            callerName={callerName}
          />

          {keypadOpen ? (
            <CallKeypad
              onDigit={sendDtmf}
              onClose={() => onKeypadOpenChange(false)}
            />
          ) : null}

          <CallControls
            view={view}
            isMuted={isMuted}
            onHangup={hangup}
            onToggleMute={toggleMute}
            keypadOpen={keypadOpen}
            onToggleKeypad={() => onKeypadOpenChange(!keypadOpen)}
            // Lead actions only make sense on an answered inbound call (CRM-905).
            leadId={isInbound && view.showMute ? activeCall.leadId : undefined}
            onCreateLead={
              isInbound && view.showMute && !activeCall.leadId
                ? onCreateLead
                : undefined
            }
            moves={moveControls({ canTransfer, onHold, openPicker })}
            voicemail={voicemailControl({
              offer: offerVoicemail,
              count: voicemails.data?.length ?? 0,
              loading: voicemails.isLoading,
              failed: voicemails.isError,
              onOpen: () => setVoicemailPickerCallId(activeCall.callId)
            })}
          />
        </div>
      )}
    </div>
  );
}

function PartyHeader({
  displayName,
  displayNumber,
  transferSource,
  addedBy,
  live
}: {
  displayName: string | undefined;
  displayNumber: string;
  transferSource: string | null;
  // CRM-1818: the teammate who added you to their call.
  addedBy: string | undefined;
  live: boolean;
}) {
  return (
    <>
      {/* Initials only for a known label; a bare number gets the silhouette
          (Jon, 2026-08-24) — it must not go into `name` or it turns into
          "+(" initials. */}
      <Avatar
        name={displayName}
        size={72}
        className="text-2xl font-semibold"
        fallbackText={<HugeiconsIcon icon={User02Icon} size={32} />}
        fallbackBackgroundColor={
          live ? ACTIVE_AVATAR_COLOR : ENDED_AVATAR_COLOR
        }
      />

      <p className="mt-4 text-base font-semibold text-content-1000">
        {displayNumber}
      </p>
      {displayName ? (
        <p className="mt-0.5 text-sm text-content-400">{displayName}</p>
      ) : null}
      {transferSource ? (
        <p className="mt-1 text-xs text-content-400">
          Transferred from {transferSource}
        </p>
      ) : null}
      {addedBy?.trim() ? (
        <p className="mt-1 text-xs text-content-400">Added by {addedBy}</p>
      ) : null}
    </>
  );
}

// The clock and its notice, or the On hold banner in their place (CRM-1818).
function CallStatus({
  view,
  notice,
  transfer,
  callerName
}: {
  view: CallDisplay;
  notice: string | null;
  transfer: ActiveTransfer | null;
  callerName: string;
}) {
  if (transfer?.phase === "on-hold")
    return <HoldBanner since={transfer.startedAt} callerName={callerName} />;
  return <StatusBlock view={view} notice={notice} />;
}

// CRM-1818: replaces the call clock while the caller is on hold.
function HoldBanner({
  since,
  callerName
}: {
  since: Date;
  callerName: string;
}) {
  const elapsed = useElapsed(since);
  return (
    <div className="mt-6 w-full rounded-xl bg-warning-50 px-4 py-3">
      <p className="flex items-center justify-center gap-1.5 text-base font-semibold tabular-nums text-warning-600">
        <HugeiconsIcon icon={PauseIcon} size={18} />
        On hold · {formatDuration(elapsed ?? 0)}
      </p>
      <p className="mt-0.5 text-xs text-content-400">
        {firstName(callerName)} hears hold music and can&apos;t hear you.
      </p>
    </div>
  );
}

function StatusBlock({
  view,
  notice
}: {
  view: CallDisplay;
  // CRM-1747: e.g. "Justin didn't answer. You're back with the caller."
  notice: string | null;
}) {
  return (
    <div className="mt-8 min-h-12">
      <p
        className={cx(
          "text-lg font-semibold tabular-nums",
          view.tone === "error" ? "text-danger-500" : "text-content-1000"
        )}
      >
        {view.statusLine}
      </p>
      {view.detail ? (
        <p className="mx-auto mt-1 max-w-[260px] text-xs text-danger-400">
          {view.detail}
        </p>
      ) : null}
      {notice ? (
        <p className="mx-auto mt-1 max-w-[280px] text-xs text-warning-600">
          {notice}
        </p>
      ) : null}
    </div>
  );
}

// A transfer card only ever describes this call — never a newer one taken
// while it was still on screen.
const ownTransferOf = (
  activeCall: ActiveCall | null,
  transfer: ActiveTransfer | null
): ActiveTransfer | null =>
  activeCall && transfer?.callId === activeCall.callId ? transfer : null;

// Which picker is open for this call, if any.
const pickerPurposeFor = (
  picker: { callId: number; purpose: TransferPanelPurpose } | null,
  activeCall: ActiveCall
): TransferPanelPurpose | null =>
  picker?.callId === activeCall.callId ? picker.purpose : null;

// Hold, Add call and Transfer go together: the same calls can move.
const moveControls = ({
  canTransfer,
  onHold,
  openPicker
}: {
  canTransfer: boolean;
  onHold: boolean;
  openPicker: (purpose: TransferPanelPurpose) => void;
}): CallControlsProps["moves"] =>
  canTransfer
    ? {
        onHold,
        onAddCall: () => openPicker("add"),
        onTransfer: () => openPicker("transfer")
      }
    : undefined;

// CRM-1830: Leave voicemail is only on an answered call this teammate placed
// and nobody has transferred, put on hold or added someone to (CRM-1818).
// Incoming calls, the mobile app and the AI agent never get it.
function canOfferVoicemail(
  activeCall: ActiveCall | null,
  view: CallDisplay,
  transfer: ActiveTransfer | null
): boolean {
  if (!activeCall || !view.showMute) return false;
  if (activeCall.direction !== "outbound") return false;
  if (activeCall.transferredByName || activeCall.transferStarted) return false;
  return transfer?.callId !== activeCall.callId;
}

// The card's Leave voicemail button, when the call offers one (CRM-1830). A
// list that failed to load stays openable: the list says so there.
function voicemailControl({
  offer,
  count,
  loading,
  failed,
  onOpen
}: {
  offer: boolean;
  count: number;
  loading: boolean;
  failed: boolean;
  onOpen: () => void;
}): CallControlsProps["voicemail"] {
  if (!offer) return undefined;
  return {
    available: count > 0 || failed,
    showEmptyHint: !loading && !failed && count === 0,
    onOpen
  };
}

// What replaces the card's body for a pre-recorded voicemail (CRM-1830): the
// message just left, or the list to pick one from. Null keeps the rest.
function voicemailBody({
  drop,
  pickerOpen,
  callerName,
  displayName,
  elapsedLabel,
  onClosePicker
}: {
  drop: ActiveVoicemailDrop | null;
  pickerOpen: boolean;
  callerName: string;
  displayName: string | undefined;
  elapsedLabel: string;
  onClosePicker: () => void;
}) {
  if (drop)
    return (
      <VoicemailLeavingStatus
        drop={drop}
        callerName={callerName}
        displayName={displayName}
      />
    );
  if (pickerOpen)
    return (
      <VoicemailPanel
        callerName={callerName}
        elapsedLabel={elapsedLabel}
        onClose={onClosePicker}
      />
    );
  return null;
}

// What replaces the card's body during a transfer (CRM-1747): the Talk first
// or Add call consult, the 3-way call (CRM-1818), how a Transfer now went, or
// the Transfer call / Add to call view. Null keeps the regular in-call body —
// also on hold, which only changes what that body shows.
function transferBody({
  transfer,
  pickerPurpose,
  callerName,
  elapsedLabel,
  durationSeconds,
  live,
  keypadOpen,
  onKeypadOpenChange,
  onDismiss,
  onClosePicker
}: {
  transfer: ActiveTransfer | null;
  pickerPurpose: TransferPanelPurpose | null;
  callerName: string;
  elapsedLabel: string;
  durationSeconds: number | null;
  live: boolean;
  keypadOpen: boolean;
  onKeypadOpenChange: (open: boolean) => void;
  onDismiss: () => void;
  onClosePicker: () => void;
}) {
  if (transfer && CONSULT_PHASES.includes(transfer.phase))
    return <ConsultView transfer={transfer} callerName={callerName} />;
  // Once this leg is gone the regular body says how the call ended.
  if (transfer && THREE_WAY_PHASES.includes(transfer.phase))
    return live ? (
      <ThreeWayView
        transfer={transfer}
        callerName={callerName}
        callerElapsedLabel={elapsedLabel}
        keypadOpen={keypadOpen}
        onKeypadOpenChange={onKeypadOpenChange}
      />
    ) : null;
  if (transfer && transfer.phase !== "on-hold")
    return (
      <TransferStatus
        transfer={transfer}
        callerName={callerName}
        durationSeconds={durationSeconds}
        onClose={onDismiss}
      />
    );
  if (pickerPurpose)
    return (
      <TransferPanel
        purpose={pickerPurpose}
        callerName={callerName}
        elapsedLabel={elapsedLabel}
        onClose={onClosePicker}
      />
    );
  return null;
}

/**
 * The card after a Transfer now (CRM-1747): the teammate's own leg ended the
 * moment the call moved, so this only reports how it went. It closes on its
 * own once the transfer settles.
 */
function TransferStatus({
  transfer,
  callerName,
  durationSeconds,
  onClose
}: {
  transfer: ActiveTransfer;
  callerName: string;
  durationSeconds: number | null;
  onClose: () => void;
}) {
  const first = firstName(transfer.targetName);
  // CallScreen shows the consult, hold and 3-way phases elsewhere, never here.
  const describe =
    TRANSFER_STATUS_COPY[transfer.phase as StatusPhase] ??
    TRANSFER_STATUS_COPY.transferred;
  const copy = describe(transfer.targetName, first, callerName);
  const done = transfer.phase === "transferred" || transfer.phase === "left";

  return (
    <div className="flex flex-col items-center py-4 text-center">
      {done ? (
        <span className="grid size-[72px] place-items-center rounded-full bg-success-50 text-success-500">
          <HugeiconsIcon icon={CheckmarkCircle02Icon} size={36} />
        </span>
      ) : (
        <Avatar name={transfer.targetName} size={72} className="text-2xl" />
      )}
      <p className="mt-4 text-base font-semibold text-content-1000">
        {copy.title}
      </p>
      <p className="mx-auto mt-1 max-w-[280px] text-sm text-content-400">
        {transfer.phase === "transferred" && durationSeconds !== null
          ? `${copy.detail} · ${formatClock(durationSeconds)}`
          : copy.detail}
      </p>
      <Button
        className="mt-6"
        size={ButtonSize.SM}
        variant={ButtonVariant.BASE}
        onClick={onClose}
      >
        Close
      </Button>
    </div>
  );
}

type StatusPhase = Exclude<
  ActiveTransfer["phase"],
  | "consult-ringing"
  | "consulting"
  | "consult-not-answered"
  | "consult-destination-left"
  | "on-hold"
  | "three-way"
  | "three-way-caller-left"
>;

const TRANSFER_STATUS_COPY: Record<
  StatusPhase,
  (
    name: string,
    first: string,
    caller: string
  ) => { title: string; detail: string }
> = {
  transferring: (_name, first, caller) => ({
    title: `Transferring to ${first}…`,
    detail: `${caller} hears ringing. If ${first} doesn't answer in 30 seconds, the call comes back to you.`
  }),
  transferred: (name) => ({
    title: `Transferred to ${name}`,
    detail: "You've left the call"
  }),
  returning: (_name, first) => ({
    title: `${first} didn't answer`,
    detail: "Bringing the caller back to you…"
  }),
  "caller-left": (_name, first) => ({
    title: "The caller hung up",
    detail: `They left before ${first} answered.`
  }),
  // CRM-1818: Leave call on a 3-way call.
  left: (name, _first, caller) => ({
    title: "You left the call",
    detail: `${caller} and ${name} are still talking.`
  })
};

const formatClock = (seconds: number) =>
  `${String(Math.floor(seconds / 60)).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;

/**
 * What the card prints for the other party: their number — or an honest
 * "Unknown caller" for an escalated call the api could not attribute
 * (CRM-1393) — and the AI agent line an inbound call came through.
 */
function describeParty(activeCall: ActiveCall): {
  displayNumber: string;
  transferSource: string | null;
} {
  if (activeCall.direction !== "inbound")
    return {
      displayNumber: formatPhoneDisplay(activeCall.toNumber),
      // An outbound call a teammate transferred to you rings in as incoming
      // (CRM-1747), so only that case names a sender here.
      transferSource: activeCall.transferredByName?.trim() || null
    };

  const callerUnknown = isCallerUnknown(
    activeCall.toNumber,
    activeCall.transferredFromNumber
  );
  return {
    displayNumber: callerUnknown
      ? UNKNOWN_CALLER_LABEL
      : formatPhoneDisplay(activeCall.toNumber),
    transferSource: transferSourceLabel(
      activeCall.transferredFromNumber,
      activeCall.transferredFromName,
      activeCall.transferredByName
    )
  };
}

interface CallControlsProps {
  view: CallDisplay;
  isMuted: boolean;
  onHangup: () => void;
  onToggleMute: () => void;
  keypadOpen: boolean;
  onToggleKeypad: () => void;
  // Inbound-only lead action: link to the matched lead…
  leadId?: number;
  // …or open the in-call Create-lead form when nothing matched.
  onCreateLead?: () => void;
  // On an answered call that can move: Hold / Resume and Add call (CRM-1818),
  // and Transfer (CRM-1747).
  moves?: {
    onHold: boolean;
    onAddCall: () => void;
    onTransfer: () => void;
  };
  // CRM-1830: Leave voicemail on an answered outgoing call; greyed out while
  // the company has no messages.
  voicemail?: {
    available: boolean;
    showEmptyHint: boolean;
    onOpen: () => void;
  };
}

function CallControls({
  view,
  onHangup,
  voicemail,
  ...rowProps
}: CallControlsProps) {
  if (!view.showHangup && !view.showMute) return null;

  return (
    <div className="mt-6 w-full space-y-5">
      {/* Up to six round controls in two rows of three (CRM-1818), then Leave
          voicemail, then End call on its own (CRM-1747). */}
      {view.showMute ? <AnsweredControls view={view} {...rowProps} /> : null}
      {voicemail ? <LeaveVoicemailButton {...voicemail} /> : null}
      {view.showHangup ? (
        <div className="flex justify-center">
          <ControlButton
            label="End call"
            onClick={onHangup}
            circleClassName="bg-danger-500 text-white hover:bg-danger-600"
            icon={CallEnd01Icon}
          />
        </div>
      ) : null}
    </div>
  );
}

// The full-width button between the round controls and End call (CRM-1830,
// option A in the mockup).
function LeaveVoicemailButton({
  available,
  showEmptyHint,
  onOpen
}: {
  available: boolean;
  showEmptyHint: boolean;
  onOpen: () => void;
}) {
  const disabled = !available;
  return (
    <div className="space-y-2">
      <button
        type="button"
        disabled={disabled}
        aria-disabled={disabled}
        onClick={onOpen}
        className={cx(
          "flex h-10 w-full items-center justify-center gap-2 rounded-lg border text-xs font-bold transition",
          disabled
            ? "cursor-not-allowed border-alpha-40 bg-alpha-20 text-content-200"
            : "border-alpha-100 bg-white text-content-1000 hover:bg-alpha-40"
        )}
      >
        <VoicemailGlyph />
        <span>Leave voicemail</span>
      </button>
      {showEmptyHint ? (
        <p className="text-xs text-content-400">
          No pre-recorded voicemails yet. An admin can add them in Phone numbers
          → Pre-recorded voicemails.
        </p>
      ) : null}
    </div>
  );
}

// Mute · Keypad · Hold, then Add call · Transfer · Contact details (CRM-1818
// mockup). A control that doesn't apply leaves its place to the next one. On
// hold, Mute and Keypad are greyed out: the caller can't hear either.
function AnsweredControls({
  view,
  isMuted,
  onToggleMute,
  keypadOpen,
  onToggleKeypad,
  leadId,
  onCreateLead,
  moves
}: Omit<CallControlsProps, "onHangup" | "voicemail">) {
  const onHold = !!moves?.onHold;
  return (
    <div className="grid grid-cols-3 gap-y-4">
      <ControlButton
        label={isMuted ? "Unmute" : "Mute"}
        onClick={onToggleMute}
        disabled={onHold}
        circleClassName={cx(
          "text-content-1000 hover:bg-alpha-100",
          isMuted ? "bg-alpha-100" : "bg-alpha-40"
        )}
        icon={isMuted ? MicOff01Icon : Mic01Icon}
      />
      {view.showKeypad ? (
        <ControlButton
          label="Keypad"
          onClick={onToggleKeypad}
          pressed={keypadOpen}
          disabled={onHold}
          circleClassName={cx(
            "text-content-1000 hover:bg-alpha-100",
            keypadOpen ? "bg-alpha-100" : "bg-alpha-40"
          )}
          icon={DialpadCircle01Icon}
        />
      ) : null}
      {moves ? (
        <>
          <HoldButton onHold={onHold} />
          <ControlButton
            label="Add call"
            onClick={moves.onAddCall}
            circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
            icon={CallAdd02Icon}
          />
          <ControlButton
            label="Transfer"
            onClick={moves.onTransfer}
            circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
            icon={ArrowDataTransferHorizontalIcon}
          />
        </>
      ) : null}
      <LeadAction leadId={leadId} onCreateLead={onCreateLead} />
    </div>
  );
}

// Hold, or a highlighted Resume while the caller is on hold (CRM-1818).
function HoldButton({ onHold }: { onHold: boolean }) {
  const { holdCall, transferAction } = useSoftphone();
  const [pending, setPending] = useState(false);

  const toggle = async () => {
    setPending(true);
    try {
      await (onHold ? transferAction("resume") : holdCall());
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  return (
    <ControlButton
      label={onHold ? "Resume" : "Hold"}
      onClick={() => void toggle()}
      pressed={onHold}
      disabled={pending}
      circleClassName={
        onHold
          ? "bg-warning-500 text-white hover:bg-warning-600"
          : "bg-alpha-40 text-content-1000 hover:bg-alpha-100"
      }
      icon={onHold ? PlayIcon : PauseIcon}
    />
  );
}

function LeadAction({
  leadId,
  onCreateLead
}: {
  leadId?: number;
  onCreateLead?: () => void;
}) {
  if (leadId)
    return (
      // Plain anchor (the overlay mounts outside the router) opening a NEW
      // tab — a same-tab navigation would unload the SPA and destroy the
      // Twilio Device, dropping the live call.
      <a
        href={`/leads/${leadId}`}
        target="_blank"
        rel="noreferrer"
        className="flex flex-col items-center gap-2"
      >
        <span className="grid size-12 place-items-center rounded-full bg-alpha-40 text-content-1000 transition hover:bg-alpha-100">
          <HugeiconsIcon icon={UserCircleIcon} size={22} />
        </span>
        <span className="text-xs text-content-400">Contact details</span>
      </a>
    );
  if (onCreateLead)
    return (
      <ControlButton
        label="Create lead"
        onClick={onCreateLead}
        circleClassName="bg-alpha-40 text-content-1000 hover:bg-alpha-100"
        icon={UserAdd01Icon}
      />
    );
  return null;
}
